import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from 'app/api';
import { setSnackbar } from './snackbarSlice';

export const getSiteList = createAsyncThunk(
  'site/getList',
  async ({ page = 1, pageSize = 50, search = '' } = {}, { dispatch, rejectWithValue }) => {
    try {
      const params = { page, pageSize };
      const trimmedSearch = typeof search === 'string' ? search.trim() : '';
      if (trimmedSearch) params.search = trimmedSearch;

      const res = await API.get('/GetSites', { params });
      const data = res?.data;
      const sites = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];

      const pagination = data && !Array.isArray(data) && typeof data === 'object'
        ? {
            page: Number.isFinite(data.page) ? data.page : page,
            pageSize: Number.isFinite(data.pageSize) ? data.pageSize : pageSize,
            totalCount: Number.isFinite(data.totalCount) ? data.totalCount : sites.length,
            totalPages: Number.isFinite(data.totalPages) ? data.totalPages : Math.max(1, Math.ceil(sites.length / pageSize)),
            hasPreviousPage: typeof data.hasPreviousPage === 'boolean' ? data.hasPreviousPage : page > 1,
            hasNextPage: typeof data.hasNextPage === 'boolean' ? data.hasNextPage : false,
          }
        : { page: 1, pageSize: sites.length, totalCount: sites.length, totalPages: 1, hasPreviousPage: false, hasNextPage: false };

      return { sites, pagination };
    } catch (err) {
      dispatch(setSnackbar({ status: 'error', message: 'Unable to retrieve Projects' }));
      return rejectWithValue(err.message);
    }
  }
);

export const getSite = createAsyncThunk(
  'site/getById',
  async (siteId, { dispatch, rejectWithValue }) => {
    try {
      const res = await API.get('/GetSiteById', { params: { SiteId: siteId } });
      return res.data;
    } catch (error) {
      if (error.response?.status === 401) {
        return rejectWithValue({ unauthorized: true });
      }
      dispatch(setSnackbar({ status: 'error', message: 'Unable to retrieve site' }));
      return rejectWithValue(error.message);
    }
  }
);

export const getSiteUsers = createAsyncThunk(
  'site/getUsers',
  async ({ siteId }, { dispatch, rejectWithValue }) => {
    try {
      const res = await API.get('/GetSiteUsersBySiteId', { params: { SiteId: siteId } });
      return res.data;
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: 'Unable to retrieve site user info' }));
      return rejectWithValue(error.message);
    }
  }
);

export const createSite = createAsyncThunk(
  'site/create',
  async (site, { dispatch, rejectWithValue }) => {
    try {
      const res = await API.post('/CreateSite', site);
      dispatch(setSnackbar({ status: 'success', message: 'Project created successfully' }));
      return res.data;
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: 'Failed to create site' }));
      return rejectWithValue(error.message);
    }
  }
);

export const updateSite = createAsyncThunk(
  'site/update',
  async ({ id: siteId, siteName, siteDescription, isActive, isArchive, isDemo, orgId }, { dispatch, rejectWithValue }) => {
    try {
      const site = { siteName, siteDescription, isActive, isArchive, isDemo };
      const res = await API.put('/UpdateSite', site, { params: { SiteId: siteId } });
      dispatch(setSnackbar({ status: 'success', message: 'Project updated successfully' }));
      if (orgId) {
        dispatch(getSiteList());
      } else {
        dispatch(getSite(siteId));
      }
      return res.data;
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: 'Failed to update site' }));
      return rejectWithValue(error.message);
    }
  }
);

export const deleteSite = createAsyncThunk(
  'site/delete',
  async (site, { dispatch, rejectWithValue }) => {
    try {
      const res = await API.delete('/DeleteSite', { params: { SiteId: site.siteId } });
      dispatch(getSiteList());
      return res.data;
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: 'Unable to delete site' }));
      return rejectWithValue(error.message);
    }
  }
);

// Runs `request(id)` for each id, 10 at a time, and splits the ids by outcome.
// Shared by the admin bulk actions, which send one request per item.
export const runInBatches = async (ids, request) => {
  const succeeded = [];
  const failed = [];
  for (let i = 0; i < ids.length; i += 10) {
    const chunk = ids.slice(i, i + 10);
    const results = await Promise.allSettled(chunk.map(request));
    results.forEach((r, j) => (r.status === 'fulfilled' ? succeeded : failed).push(chunk[j]));
  }
  return { succeeded, failed };
};

export const bulkSnackbar = (dispatch, { succeeded, failed }, verb, noun) => {
  const plural = (n) => `${n} ${noun}${n === 1 ? '' : 's'}`;
  dispatch(
    setSnackbar(
      failed.length
        ? { status: 'error', message: `${verb} ${plural(succeeded.length)}; ${failed.length} failed` }
        : { status: 'success', message: `${verb} ${plural(succeeded.length)}` }
    )
  );
};

// Admin bulk actions for the Projects page. The caller refetches the list so
// it keeps its current page and search.
export const bulkAddSitesToOrg = createAsyncThunk(
  'site/bulkAddToOrg',
  async ({ orgId, siteIds }, { dispatch }) => {
    const result = await runInBatches(siteIds, (siteId) =>
      API.post('/AddSiteToOrg', null, { params: { OrgId: orgId, SiteId: siteId } })
    );
    bulkSnackbar(dispatch, result, 'Moved', 'project');
    return result;
  }
);

export const bulkDeleteSites = createAsyncThunk(
  'site/bulkDelete',
  async ({ siteIds }, { dispatch }) => {
    const result = await runInBatches(siteIds, (siteId) =>
      API.delete('/DeleteSite', { params: { SiteId: siteId } })
    );
    bulkSnackbar(dispatch, result, 'Deleted', 'project');
    return result;
  }
);

export const inviteUserToSite = createAsyncThunk(
  'site/inviteUser',
  async ({ siteId, userEmail, createdBy, siteRole }, { dispatch, rejectWithValue }) => {
    try {
      const res = await API.post('/InviteUserToSite', {
        siteId,
        userEmail,
        createdBy,
        siteRole,
      });
      dispatch(setSnackbar({ status: 'success', message: 'User added to site' }));
      dispatch(getSiteUsers({ siteId }));
      return res.data;
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: 'Failed to Add User to site' }));
      return rejectWithValue(error.message);
    }
  }
);

export const updateSiteImage = createAsyncThunk(
  'site/updateImage',
  async ({ photo, siteId }, { dispatch, rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', photo);
      const res = await API.post('/UploadProjectPhoto', formData, { params: { SiteId: siteId } });
      dispatch(setSnackbar({ status: 'success', message: 'Image uploaded successfully' }));
      dispatch(getSite(siteId));
      return res.data;
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: 'Failed to upload image' }));
      return rejectWithValue(error.message);
    }
  }
);

const siteSlice = createSlice({
  name: 'site',
  initialState: {
    site: {},
    siteList: [],
    siteListPagination: {
      page: 1,
      pageSize: 50,
      totalCount: 0,
      totalPages: 1,
      hasPreviousPage: false,
      hasNextPage: false,
    },
    siteUsersList: [],
    loading: false,
    error: false,
    unauthorized: false,
  },
  reducers: {
    clearSite: (state) => {
      state.site = {};
      state.unauthorized = false;
    },
    clearSiteUsers: (state) => {
      state.siteUsersList = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSiteList.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(getSiteList.fulfilled, (state, action) => {
        state.loading = false;
        state.siteList = action.payload.sites;
        state.siteListPagination = action.payload.pagination;
      })
      .addCase(getSiteList.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      .addCase(getSite.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(getSite.fulfilled, (state, action) => {
        state.loading = false;
        state.site = action.payload;
        state.unauthorized = false;
      })
      .addCase(getSite.rejected, (state, action) => {
        state.loading = false;
        if (action.payload?.unauthorized) {
          state.unauthorized = true;
        } else {
          state.error = true;
        }
      })
      .addCase(getSiteUsers.fulfilled, (state, action) => {
        state.siteUsersList = action.payload;
      })
      .addCase(createSite.pending, (state) => {
        state.loading = true;
      })
      .addCase(createSite.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createSite.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });
  },
});

export const { clearSite, clearSiteUsers } = siteSlice.actions;
export default siteSlice.reducer;
